import { format } from 'date-fns';
import { HoldingMessage } from '../hooks/useStreamChat';
import { ParsedStreamChatMessage } from '../types';

/**
 * Applies an incoming `HoldingMessage` to the existing list of `ParsedStreamChatMessage`s.
 *
 * The logic mirrors the inline implementation that previously lived in `useStreamChat`:
 * 1. Ignore non-holding messages (safety check – not expected from the updates stream).
 * 2. If a message with `originalId` matching the incoming one already exists ➜ update it.
 * 3. Otherwise, create a new message object and
 *    – insert it right before the currently streaming message (if one exists), or
 *    – append it to the end of the array.
 */
export const applyHoldingMessage = (
  prev: ParsedStreamChatMessage[],
  message: HoldingMessage,
): ParsedStreamChatMessage[] => {
  if (message.type !== 'holding') return prev;

  // 1. If we already rendered a message originating from this holding id – update it.
  const duplicateIndex = prev.findIndex((msg) => msg.originalId === message.id);
  if (duplicateIndex !== -1) {
    const updated = [...prev];
    updated[duplicateIndex] = {
      ...updated[duplicateIndex],
      content: message.content,
      timestamp: format(new Date(), 'HH:mm'),
      isStreaming: false,
    };
    return updated;
  }

  // 2. Otherwise construct a fresh message to display.
  const newMessage: ParsedStreamChatMessage = {
    id: String(Date.now()),
    content: message.content,
    timestamp: format(new Date(), 'HH:mm'),
    isUser: false,
    isStreaming: false,
    originalId: message.id,
  } as ParsedStreamChatMessage;

  // 3. Insert the new message right before the currently streaming bot message if present.
  const streamingIndex = prev.findIndex((msg) => msg.isStreaming);
  if (streamingIndex !== -1) {
    return [
      ...prev.slice(0, streamingIndex),
      newMessage,
      ...prev.slice(streamingIndex),
    ];
  }

  // 4. Fallback – append to the end of the list.
  return [...prev, newMessage];
};
